import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOrganization } from './OrganizationContext';
import { environmentService } from '@/services/environmentService';
import type { Environment, EnvironmentCreate, EnvironmentUpdate } from '@/types/environment';

/**
 * Environments are org-scoped. This context depends on OrganizationContext
 * (not ProjectContext — that's Phase 2 of the ProjectEnvironment refactor).
 *
 * For the actual deployment target (Project × Environment), see
 * ProjectEnvironmentContext.
 */
interface EnvironmentContextType {
  currentEnvironment: Environment | null;
  environments: Environment[];
  isLoading: boolean;
  error: string | null;
  setCurrentEnvironment: (environment: Environment | null) => void;
  createEnvironment: (data: Omit<EnvironmentCreate, 'organizationId'>) => Promise<Environment>;
  updateEnvironment: (environmentId: string, data: EnvironmentUpdate) => Promise<Environment>;
  deleteEnvironment: (environmentId: string) => Promise<void>;
  refreshEnvironments: () => Promise<void>;
}

const EnvironmentContext = createContext<EnvironmentContextType | null>(null);

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

interface EnvironmentProviderProps {
  children: React.ReactNode;
}

export const EnvironmentProvider = ({ children }: EnvironmentProviderProps) => {
  const { currentOrganization } = useOrganization();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [currentEnvironment, setCurrentEnvironment] = useState<Environment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEnvironments = useCallback(async () => {
    if (!currentOrganization) {
      setEnvironments([]);
      setCurrentEnvironment(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loadedEnvironments = await environmentService.getEnvironments(currentOrganization.id);
      setEnvironments(loadedEnvironments);

      const savedEnvId = localStorage.getItem(`gizmodata-current-env-${currentOrganization.id}`);
      if (savedEnvId) {
        const savedEnv = loadedEnvironments.find(e => e.id === savedEnvId);
        if (savedEnv) {
          setCurrentEnvironment(savedEnv);
        } else if (loadedEnvironments.length > 0) {
          setCurrentEnvironment(loadedEnvironments[0]);
        } else {
          setCurrentEnvironment(null);
        }
      } else if (loadedEnvironments.length > 0) {
        setCurrentEnvironment(loadedEnvironments[0]);
      } else {
        setCurrentEnvironment(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load environments');
      setEnvironments([]);
      setCurrentEnvironment(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    loadEnvironments();
  }, [loadEnvironments]);

  // Only save an environment that belongs to the current org: right after an org switch the old org's
  // environment is still selected, and saving it would overwrite the new org's remembered one.
  useEffect(() => {
    if (currentOrganization && currentEnvironment && currentEnvironment.organizationId === currentOrganization.id) {
      localStorage.setItem(`gizmodata-current-env-${currentOrganization.id}`, currentEnvironment.id);
    }
  }, [currentOrganization, currentEnvironment]);

  const handleSetCurrentEnvironment = (environment: Environment | null) => {
    setCurrentEnvironment(environment);
  };

  const createEnvironment = async (data: Omit<EnvironmentCreate, 'organizationId'>): Promise<Environment> => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    const newEnvironment = await environmentService.createEnvironment({
      ...data,
      organizationId: currentOrganization.id,
    });

    setEnvironments(prev => [...prev, newEnvironment]);

    if (environments.length === 0) {
      setCurrentEnvironment(newEnvironment);
    }

    return newEnvironment;
  };

  const updateEnvironment = async (environmentId: string, data: EnvironmentUpdate): Promise<Environment> => {
    const updatedEnvironment = await environmentService.updateEnvironment(environmentId, data);

    setEnvironments(prev => prev.map(e => (e.id === environmentId ? updatedEnvironment : e)));

    if (currentEnvironment?.id === environmentId) {
      setCurrentEnvironment(updatedEnvironment);
    }

    return updatedEnvironment;
  };

  const deleteEnvironment = async (environmentId: string): Promise<void> => {
    await environmentService.deleteEnvironment(environmentId);

    setEnvironments(prev => prev.filter(e => e.id !== environmentId));

    if (currentEnvironment?.id === environmentId) {
      const remaining = environments.filter(e => e.id !== environmentId);
      setCurrentEnvironment(remaining.length > 0 ? remaining[0] : null);
    }
  };

  const refreshEnvironments = async (): Promise<void> => {
    await loadEnvironments();
  };

  return (
    <EnvironmentContext.Provider
      value={{
        currentEnvironment,
        environments,
        isLoading,
        error,
        setCurrentEnvironment: handleSetCurrentEnvironment,
        createEnvironment,
        updateEnvironment,
        deleteEnvironment,
        refreshEnvironments,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};
